// core/guards/auth.guard.ts
import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { map, take, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { devError, devWarn } from '@core';

/**
 * AuthGuard - Ensures user is authenticated before accessing protected routes
 *
 * Usage:
 * - Apply to routes that require authentication (check-ins, contacts, settings, etc.)
 * - If user is not authenticated, redirect to /onboarding
 * - If user is authenticated, allow access
 *
 * Security Strategy: FAIL CLOSED
 * - On error checking auth state, redirect to /onboarding (deny access)
 * - Rationale: Authentication guards must never allow unauthenticated access
 * - Attack scenario: Attacker could trigger Firebase Auth errors to bypass authentication
 * - User impact: Offline users must re-authenticate when network recovers
 *
 * Error Handling:
 * - Firebase Auth errors: Fail closed by redirecting to /onboarding
 * - This ensures unauthorized users cannot access protected routes under any circumstances
 * - Consistent with OnboardingGuard's fail-closed strategy
 * - See specs_and_project_plan/security-compliance.md for security requirements
 */
export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.authState$.pipe(
    take(1), // FIX HIGH-01: Only take first emission to prevent memory leaks during rapid navigation
    map((user) => {
      if (user) {
        // User is authenticated, allow access
        return true;
      } else {
        // User is not authenticated, redirect to onboarding
        return router.createUrlTree(['/onboarding']);
      }
    }),
    catchError((error) => {
      devError('[AuthGuard] Error checking auth state:', error);

      // SECURITY FIX: Fail closed - redirect to onboarding instead of allowing access
      // This prevents unauthorized access if Firebase Auth errors can be triggered by attackers
      devWarn('[AuthGuard] Redirecting to onboarding due to auth check error (fail closed)');
      return of(router.createUrlTree(['/onboarding']));
    })
  );
};
